import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, BookOpen, Headphones, Mic, Trophy } from 'lucide-react';
import { KITT } from '@/constants/hedgekit';

const STEPS = [
  {
    title: "Welcome to Kitt's Book Nook!",
    description: "I'm Kitt, your reading buddy! Let me show you around.",
    icon: null,
    image: true
  },
  {
    title: "Choose Your Reading Level",
    description: "Pick a band that feels right - Short Stories, Chapter Books, or Big Adventures!",
    icon: BookOpen
  },
  {
    title: "Three Ways to Read",
    description: "Read to Me (I read aloud), Read With Me (we read together), or I'll Read (you read, I listen).",
    icon: Headphones
  },
  {
    title: "Earn Achievements",
    description: "The more you read, the more badges you'll earn. Check your stats anytime!",
    icon: Trophy
  }
];

export default function Tutorial({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleComplete = () => {
    localStorage.removeItem('kbn_show_tutorial');
    setVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.removeItem('kbn_show_tutorial');
    setVisible(false);
    onComplete?.();
  };

  if (!visible) return null;

  const currentStep = STEPS[step];
  const Icon = currentStep.icon;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-center relative">
          <button
            onClick={handleSkip}
            className="absolute top-3 right-3 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Skip tutorial"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          {currentStep.image ? (
            <img 
              src={KITT.waving} 
              alt="Kitt" 
              className="w-24 h-24 mx-auto object-contain"
            />
          ) : Icon ? (
            <div className="w-20 h-20 mx-auto bg-white/20 rounded-full flex items-center justify-center">
              <Icon className="w-10 h-10 text-white" />
            </div>
          ) : null}
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h3 className="text-xl font-heading font-bold mb-2">{currentStep.title}</h3>
          <p className="text-muted-foreground">{currentStep.description}</p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={step === 0}
            className="gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-muted-foreground"
          >
            Skip
          </Button>
          
          <Button onClick={handleNext} className="gap-1">
            {step === STEPS.length - 1 ? "Let's Go!" : 'Next'}
            {step < STEPS.length - 1 && <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
